import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { isNil } from 'lodash'
import { WorkOrderIndexEntry, WorkOrderIndexEntryConfig } from '../../types'
import { tableSortingCompareValues, formatUTCAsLocalDateTime } from 'utils'
import {
  MANAGER_LIST_SECTION_DEFAULT_LIMIT,
  MANAGER_INDEX_PAGE_SIZE,
} from '../constants'

const VIEW_TYPE_LIST = 'list'
const VIEW_TYPE_INDEX = 'index'

const propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(WorkOrderIndexEntryConfig).isRequired,
  rows: PropTypes.arrayOf(WorkOrderIndexEntry).isRequired,
  // Controls how the table renders:
  // - 'list' (default): caps rows at MANAGER_LIST_SECTION_DEFAULT_LIMIT and
  //   exposes a "View All" / "View Less" toggle to expand the section in place.
  //   Used by the per-list sections on the Work Order Management tab.
  // - 'index': splits rows into client-side pages of `pageSize` with
  //   previous/next controls. Used by the whole-list index (the tab labeled
  //   "View All" in the UI). The backend request is unchanged; this only pages
  //   the already-fetched rows.
  viewType: PropTypes.oneOf([VIEW_TYPE_LIST, VIEW_TYPE_INDEX]),
  // Page size for the 'index' viewType. Ignored for 'list'.
  pageSize: PropTypes.number,
  // Changing this value returns the table to the first page. Callers pass the
  // normalized value of whatever narrows `rows` (e.g. a search term) so that
  // filtering does not strand the user on a trailing page of the old result
  // set. It is deliberately not derived from `rows`, whose array identity
  // changes on every upstream render.
  pageResetKey: PropTypes.string,
  // Optional per-row override for a column's text color. Receives (row, columnKey)
  // and may return a CSS color string or null/undefined to fall back to the column
  // default. Used by the index Status column to color by status type.
  getCellTextColor: PropTypes.func,
}

const defaultProps = {
  viewType: VIEW_TYPE_LIST,
  pageSize: MANAGER_INDEX_PAGE_SIZE,
  pageResetKey: '',
}

function WorkOrderTableSection({
  title,
  columns,
  rows,
  viewType,
  pageSize,
  pageResetKey,
  getCellTextColor,
}) {
  const [sortKey, setSortKey] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [viewAll, setViewAll] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)

  const sortedOrders = useMemo(() => {
    if (!sortKey) return rows
    return [...rows].sort((orderA, orderB) =>
      tableSortingCompareValues(orderA[sortKey], orderB[sortKey], sortDirection)
    )
  }, [rows, sortKey, sortDirection])

  const isIndexView = viewType === VIEW_TYPE_INDEX
  const effectivePageSize = pageSize > 0 ? pageSize : MANAGER_INDEX_PAGE_SIZE

  const totalPages = isIndexView
    ? Math.max(1, Math.ceil(sortedOrders.length / effectivePageSize))
    : 1

  // Clamp the stored page into range at render time rather than resetting it via
  // an effect. `rows` is re-derived on every render upstream, so depending on it
  // in an effect would bounce the user back to page 1 on unrelated re-renders.
  const safePage = Math.min(currentPage, totalPages - 1)

  // Reset to the first page only on deliberate changes (sort, page size, or an
  // explicit reset key), all of which are stable values and never tied to the
  // `rows` array identity.
  useEffect(() => {
    setCurrentPage(0)
  }, [sortKey, sortDirection, effectivePageSize, pageResetKey])

  const displayedOrders = useMemo(() => {
    if (isIndexView) {
      const start = safePage * effectivePageSize
      return sortedOrders.slice(start, start + effectivePageSize)
    }
    return viewAll
      ? sortedOrders
      : sortedOrders.slice(0, MANAGER_LIST_SECTION_DEFAULT_LIMIT)
  }, [sortedOrders, viewAll, isIndexView, effectivePageSize, safePage])

  const handleSort = (col) => {
    if (col.sortable === false) return
    const key = col.key
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const sortIndicator = (col) => {
    if (col.sortable === false) return null
    if (sortKey !== col.key) {
      return (
        <span className="data-table__sort" aria-hidden>
          ↕
        </span>
      )
    }
    return (
      <span className="data-table__sort" aria-hidden>
        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
      </span>
    )
  }

  return (
    <section className="card-styling manager-dashboard__section">
      {title && <h2 className="card-styling__title">{title}</h2>}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="data-table__th"
                  onClick={() => handleSort(col)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      // Prevent scrolling when space is pressed
                      e.preventDefault()
                      handleSort(col)
                    }
                  }}
                  // Accessibility / screens reader hints for sorting
                  role={col.sortable !== false ? 'button' : undefined}
                  tabIndex={col.sortable !== false ? 0 : undefined}
                >
                  {col.label}
                  {sortIndicator(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedOrders.map((workOrder) => (
              <tr key={workOrder['id']}>
                {columns.map((columnSetting) => {
                  const rawValue = workOrder[columnSetting.key]
                  const isEmpty = isNil(rawValue)
                  const isDateTimeColumn = columnSetting.isDateTime === true

                  let value
                  if (isEmpty) {
                    value = columnSetting.fallback
                  } else if (isDateTimeColumn) {
                    const formatted = formatUTCAsLocalDateTime(rawValue)
                    value =
                      formatted != null ? formatted : columnSetting.fallback
                  } else {
                    value = rawValue
                  }

                  const dynamicTextColor = getCellTextColor
                    ? getCellTextColor(workOrder, columnSetting.key)
                    : undefined
                  const resolvedTextColor =
                    dynamicTextColor ?? columnSetting.textColor
                  const cellStyle = resolvedTextColor
                    ? { color: resolvedTextColor }
                    : undefined
                  const cellContent =
                    columnSetting.link === true ? (
                      <Link
                        to={`/manager/orders/${workOrder.id}`}
                        style={cellStyle}
                        className="data-table__link"
                      >
                        {value}
                      </Link>
                    ) : (
                      value
                    )
                  return (
                    <td
                      key={columnSetting.key + '-' + workOrder['id']}
                      className="data-table__td"
                      style={columnSetting.link ? undefined : cellStyle}
                    >
                      {cellContent}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isIndexView && totalPages > 1 && (
        <nav
          className="manager-dashboard__pagination"
          aria-label="Work orders pagination"
        >
          <button
            type="button"
            className="manager-dashboard__pagination-btn"
            onClick={() => setCurrentPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
          >
            Previous
          </button>
          <span className="manager-dashboard__pagination-status">
            Page {safePage + 1} of {totalPages}
          </span>
          <button
            type="button"
            className="manager-dashboard__pagination-btn"
            onClick={() =>
              setCurrentPage(Math.min(totalPages - 1, safePage + 1))
            }
            disabled={safePage >= totalPages - 1}
          >
            Next
          </button>
        </nav>
      )}
      {!isIndexView && rows.length > MANAGER_LIST_SECTION_DEFAULT_LIMIT && (
        <button
          type="button"
          className="card-styling__action"
          onClick={() => setViewAll(!viewAll)}
        >
          {viewAll ? 'View Less' : 'View All'}
        </button>
      )}
    </section>
  )
}

WorkOrderTableSection.propTypes = propTypes
WorkOrderTableSection.defaultProps = defaultProps

export default WorkOrderTableSection
